import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaCalendarAlt, FaMapMarkerAlt, FaPlus } from 'react-icons/fa';
import { supabase } from '../data/supabaseClient';
import { AuthContext } from '../Provider';
import EventCard from './components/EventCard';
import EventFilters from './components/EventFilters';
import Spinner from '../common/Spinner';
import { CustomButton2, CustomButton8 } from '../Templates';
import { useToast } from '../hooks/ToastContext';
import './styles/EventsPage.css';

function EventsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { user } = useContext(AuthContext);
  const { addToast } = useToast();
  
  // State
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    searchTerm: '',
    dateFrom: null,
    dateTo: null,
    category: 'all',
  });

  // Try to fetch event creator details separately if needed
  const fetchEventCreators = async (events) => {
    if (!events || events.length === 0) return events;
    
    try {
      // Extract creator IDs
      const creatorIds = [...new Set(events.map(event => event.creator_id))];
      
      if (creatorIds.length === 0) return events;
      
      // Fetch profiles for these creators
      const { data: creators } = await supabase
        .from('profiles')
        .select('id, username, avatar_url')
        .in('id', creatorIds);
      
      if (!creators) return events;
      
      // Attach creator info to events
      return events.map(event => ({
        ...event,
        creator: creators.find(c => c.id === event.creator_id) || { id: event.creator_id }
      }));
    } catch (err) {
      console.error('Error fetching event creators:', err);
      return events;
    }
  };

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          throw error;
        }
        
        if (!data.session) {
          navigate('/login');
          return;
        }
        
        // Fetch events after confirming session
        fetchEvents();
      } catch (err) {
        console.error('Session check error:', err);
        setError(err.message);
        
        // Redirect to login after delay
        setTimeout(() => navigate('/login'), 3000);
      }
    };
    
    checkSession();
  }, [navigate]);

  const fetchEvents = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Modified query approach - separate queries
      let eventsQuery = supabase
        .from('events')
        .select('*')
        .gte('start_date', new Date().toISOString())
        .order('start_date', { ascending: true });
      
      // Apply filters if set
      if (filters.searchTerm) {
        eventsQuery = eventsQuery.or(`title.ilike.%${filters.searchTerm}%,location.ilike.%${filters.searchTerm}%`);
      }
      
      if (filters.dateFrom) {
        eventsQuery = eventsQuery.gte('start_date', filters.dateFrom.toISOString());
      }
      
      if (filters.dateTo) {
        eventsQuery = eventsQuery.lte('start_date', filters.dateTo.toISOString());
      }
      
      if (filters.category && filters.category !== 'all') {
        eventsQuery = eventsQuery.eq('category', filters.category);
      }
      
      // Execute query
      const { data: eventsData, error: eventsError } = await eventsQuery.limit(20);
      
      if (eventsError) {
        throw eventsError;
      }

      // If no events, return early
      if (!eventsData || eventsData.length === 0) {
        setEvents([]);
        setIsLoading(false);
        return;
      }

      // Get creator info
      const eventsWithCreators = await fetchEventCreators(eventsData);
      
      // Get event IDs for attendees query
      const eventIds = eventsData.map(event => event.id);
      
      // Separate query for attendees
      const { data: attendeesData, error: attendeesError } = await supabase
        .from('event_attendees')
        .select('*')
        .in('event_id', eventIds);
        
      if (attendeesError) {
        console.error('Error fetching attendees:', attendeesError);
        // Continue without attendees data
      }
      
      // Group attendees by event
      const attendeesByEvent = {};
      if (attendeesData) {
        attendeesData.forEach(attendee => {
          if (!attendeesByEvent[attendee.event_id]) {
            attendeesByEvent[attendee.event_id] = [];
          }
          attendeesByEvent[attendee.event_id].push(attendee);
        });
      }
      
      // Process events to add computed properties
      const processedEvents = eventsWithCreators.map(event => ({
        ...event,
        attendees: attendeesByEvent[event.id] || [],
        attendeeCount: (attendeesByEvent[event.id] || []).length,
        isAttending: (attendeesByEvent[event.id] || []).some(
          a => a.user_id === user?.id && a.status === 'going'
        ),
        isInterested: (attendeesByEvent[event.id] || []).some(
          a => a.user_id === user?.id && a.status === 'interested'
        )
      }));
      
      setEvents(processedEvents);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(err.message);
      
      addToast({
        type: 'error',
        title: t('errors.fetch_error'),
        description: t('errors.event_fetch_failed')
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters
    }));
    
    // Re-fetch with new filters
    fetchEvents();
  };

  const handleCreateEvent = () => {
    // Navigate to event creation page (to be implemented)
    navigate('/events/create');
  };

  const handleBackClick = () => {
    navigate('/home');
  };

  const handleAttendEvent = async (eventId, status) => {
    try {
      // Check if already attending/interested
      const existingRecord = events.find(e => e.id === eventId)?.attendees
        ?.find(a => a.user_id === user.id);
      
      if (existingRecord) {
        // Update status if already exists
        const { error } = await supabase
          .from('event_attendees')
          .update({ status })
          .eq('event_id', eventId)
          .eq('user_id', user.id);
          
        if (error) throw error;
      } else {
        // Insert new record
        const { error } = await supabase
          .from('event_attendees')
          .insert({
            event_id: eventId,
            user_id: user.id,
            status
          });
          
        if (error) throw error;
      }
      
      // Update local state
      setEvents(prev => prev.map(event => {
        if (event.id === eventId) {
          let newAttendees = [...(event.attendees || [])];
          const existingIndex = newAttendees.findIndex(a => a.user_id === user.id);
          
          if (existingIndex >= 0) {
            newAttendees[existingIndex] = { ...newAttendees[existingIndex], status };
          } else {
            newAttendees.push({ user_id: user.id, status });
          }
          
          return {
            ...event,
            attendees: newAttendees,
            attendeeCount: status === 'going' && existingIndex < 0 ? event.attendeeCount + 1 : event.attendeeCount,
            isAttending: status === 'going',
            isInterested: status === 'interested'
          };
        }
        return event;
      }));
      
      addToast({
        type: 'success',
        title: t('events.attendance_updated'),
        description: status === 'going' ? 
          t('events.successfully_joined') : 
          t('events.marked_as_interested')
      });
    } catch (err) {
      console.error('Error updating attendance:', err);
      
      addToast({
        type: 'error',
        title: t('errors.update_failed'),
        description: t('errors.attendance_update_failed')
      });
    }
  };

  // Handle offering a ride to an event
  const handleOfferRide = (eventId) => {
    // Navigate to event ride form with offer mode
    navigate(`/events/${eventId}/ride?mode=offer`);
  };

  // Handle looking for a ride to an event
  const handleLookingForRide = (eventId) => {
    // Navigate to event ride form with request mode
    navigate(`/events/${eventId}/ride?mode=request`);
  };

  if (isLoading) {
    return (
      <div className="events-loading-container">
        <Spinner 
          size="large" 
          customText={t('app.loading')}
          fullPage
        />
      </div>
    );
  }

  return (
    <div className="events-page">
        <div className="events-header">
          <div className="back-button-container">
            <CustomButton8 onClick={handleBackClick}>
              <FaArrowLeft />
            </CustomButton8>
          </div>
          <h1>{t('events.title')}</h1>
          <div className="create-event-button">
            <CustomButton2 
              label={t('events.create_event')} 
              onClick={handleCreateEvent}
              icon={<FaPlus />}
            />
          </div>
        </div>
        
        <EventFilters 
          filters={filters} 
          onFiltersChange={handleFiltersChange} 
        />
        
        {error ? (
          <div className="events-error">
            <h3>{t('errors.error_title')}</h3>
            <p>{error}</p>
            <CustomButton2 
              label={t('buttons.retry')} 
              onClick={fetchEvents}
            />
          </div>
        ) : events.length === 0 ? (
          <div className="no-events">
            <FaCalendarAlt className="no-events-icon" />
            <h3>{t('events.no_events_found')}</h3>
            <p>{t('events.try_different_filters')}</p>
          </div>
        ) : (
          <div className="events-grid">
            {events.map(event => (
              <EventCard 
                key={event.id}
                event={event}
                onAttend={(status) => handleAttendEvent(event.id, status)}
                onViewDetails={() => navigate(`/events/${event.id}`)}
                onOfferRide={() => handleOfferRide(event.id)}
                onLookingForRide={() => handleLookingForRide(event.id)}
              />
            ))}
          </div>
        )}
      </div>
  );
}

export default EventsPage;